import styled from '@emotion/styled';
import { tokens, DropdownMenu, Text, Field, Stack, PlanBadge } from '@hotjar/hotsauce';
import { useCallback, useMemo } from 'react';
import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import { AdvancedSelect } from '@hotjar/feedback/components/common/AdvancedSelect/AdvancedSelect';
import { ReactionIcon } from '@hotjar/feedback';
import { useTranslation } from '@hotjar/translations';

const AppearanceField = styled(Field)`
  margin-bottom: ${tokens.spacer.x2};
`;

const SelectContainer = styled(Stack)`
  align-items: center;
`;

const SelectLabel = styled.div``;

const ItemContainer = styled(Stack)`
  align-items: center;
  width: 100%;
`;

const StyledFeatureGroupBadge = styled(PlanBadge)`
  margin-left: auto;
`;

export type EmotionValue = 'default' | 'emoji' | 'smiley' | 'star';

export interface EmotionStyleOption {
  value: EmotionValue;
  labelKey: string;
  feature: string | boolean;
}

export interface EmotionStyleSelectorProps {
  emotionStyle: EmotionValue;
  emotionStyleOptions: EmotionStyleOption[];
  featurePlan: FeatureGroup | null;
  onEmotionStyleChanged: (newEmotion: EmotionStyleOption) => void;
  onReloadPreview: (section: string) => void;
  hasEmotionStyleGate: boolean | null;
}

export function EmotionStyleSelector({
  emotionStyle,
  emotionStyleOptions = [],
  featurePlan,
  onEmotionStyleChanged,
  onReloadPreview,
  hasEmotionStyleGate,
}: EmotionStyleSelectorProps): JSX.Element {
  const { t } = useTranslation('feedback');
  const getEmotionStyleOption = useCallback(
    (emotionStyleValue: EmotionStyleOption['value']) =>
      emotionStyleOptions.find(({ value }) => value === emotionStyleValue) as EmotionStyleOption,
    [emotionStyleOptions],
  );

  const selectedEmotionStyleOption = useMemo(
    () => getEmotionStyleOption(emotionStyle),
    [emotionStyle, getEmotionStyleOption],
  );

  const selectLabel = t('feedback-wizard.appearance.reactions.label');

  return (
    <AppearanceField inputId="ratingStyleSelector" labelText={selectLabel} size="m">
      <AdvancedSelect
        size="m"
        ariaLabel={selectLabel}
        selectContent={
          <SelectContainer
            id="ratingStyleSelector"
            onMouseEnter={() => onReloadPreview('emotion')}
            spacing="x1"
          >
            <ReactionIcon reactionStyle={selectedEmotionStyleOption.value} />
            <SelectLabel>
              {t(`feedback-wizard.appearance.reactions.${selectedEmotionStyleOption.labelKey}`)}
            </SelectLabel>
          </SelectContainer>
        }
      >
        {emotionStyleOptions.map(({ value, labelKey, feature }) => (
          <DropdownMenu.BaseItem
            isSelected={selectedEmotionStyleOption.value === value}
            onClick={() => onEmotionStyleChanged(getEmotionStyleOption(value))}
            key={value}
          >
            <ItemContainer spacing="x1">
              <ReactionIcon reactionStyle={value} />
              <Text>{t(`feedback-wizard.appearance.reactions.${labelKey}`)}</Text>
              {feature !== false && featurePlan && !hasEmotionStyleGate && (
                <StyledFeatureGroupBadge kind={featurePlan} size="s" />
              )}
            </ItemContainer>
          </DropdownMenu.BaseItem>
        ))}
      </AdvancedSelect>
    </AppearanceField>
  );
}
